import React, { useState, useEffect } from 'react';
import './Assessments.css';

function Assessments() {
  const [assessments, setAssessments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  useEffect(() => {
    fetchAssessments();
    fetchStats();
  }, [filter]);

  const fetchAssessments = async () => {
    setLoading(true);
    try {
      const url = filter === 'all' 
        ? `${import.meta.env.VITE_API_URL}/api/assessment/admin/assessments`
        : `${import.meta.env.VITE_API_URL}/api/assessment/admin/assessments?type=${filter}`;
      
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setAssessments(data.data);
      }
    } catch (error) {
      console.error('Error fetching assessments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/assessment/admin/stats`);
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const getAssessmentTypeLabel = (type) => {
    const labels = {
      'ai-readiness': 'AI Readiness',
      'technology': 'Technology',
      'operating-model': 'Operating Model',
      'process-optimization': 'Process Optimization',
      'digital-transformation': 'Digital Transformation'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading assessments...</p>
      </div>
    );
  }

  return (
    <div className="assessments-admin">
      <div className="admin-header">
        <h1>Assessment Results</h1>
      </div>

      {stats && (
        <div className="admin-assessment-stats">
          <h3>Overview Statistics</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <h3>{stats.total}</h3>
              <p>Total Assessments</p>
            </div>
            <div className="stat-card">
              <h3>{stats.averageScore?.toFixed(1) || '0'}</h3>
              <p>Average Score</p>
            </div>
          </div>
          
          <div className="stats-breakdown">
            <div className="stats-section">
              <h4>By Type:</h4>
              <div className="stats-list">
                {stats.byType && stats.byType.map((item) => (
                  <div key={item._id} className="stat-item">
                    <span>{getAssessmentTypeLabel(item._id)}</span>
                    <strong>{item.count}</strong>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="stats-section">
              <h4>By Result:</h4>
              <div className="stats-list">
                {stats.byResult && stats.byResult.map((item) => (
                  <div key={item._id} className="stat-item">
                    <span>{item._id}</span>
                    <strong>{item.count}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <label>Filter by Type: </label>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">All Assessments</option>
          <option value="ai-readiness">AI Readiness</option>
          <option value="technology">Technology</option>
          <option value="operating-model">Operating Model</option>
          <option value="process-optimization">Process Optimization</option>
          <option value="digital-transformation">Digital Transformation</option>
        </select>
      </div>

      <div className="assessments-list">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Company</th>
              <th>Assessment Type</th>
              <th>Score</th>
              <th>Result</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assessments.length > 0 ? (
              assessments.map((assessment) => (
                <tr key={assessment._id}>
                  <td>{assessment.fullName || 'N/A'}</td>
                  <td>{assessment.email || 'N/A'}</td>
                  <td>{assessment.companyName || 'N/A'}</td>
                  <td>
                    <span className={`assessment-type-badge ${assessment.assessmentType}`}>
                      {getAssessmentTypeLabel(assessment.assessmentType)}
                    </span>
                  </td>
                  <td><strong>{assessment.totalScore || 0}/100</strong></td>
                  <td>
                    <span className={`status-badge status-${(assessment.resultCategory || 'pending').toLowerCase().replace(' ', '-')}`}>
                      {assessment.resultCategory || 'Pending'}
                    </span>
                  </td>
                  <td>{assessment.submittedAt ? new Date(assessment.submittedAt).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    <button 
                      className="view-btn" 
                      onClick={() => setSelectedAssessment(assessment)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">No assessments found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedAssessment && (
        <div className="modal-overlay" onClick={() => setSelectedAssessment(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assessment Details</h2>
              <button className="close-btn" onClick={() => setSelectedAssessment(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="details-section">
                <h3>Participant Information</h3>
                <div className="details-grid">
                  <p><strong>Name:</strong> {selectedAssessment.fullName || 'N/A'}</p>
                  <p><strong>Email:</strong> {selectedAssessment.email || 'N/A'}</p>
                  <p><strong>Phone:</strong> {selectedAssessment.phoneNumber || 'N/A'}</p>
                  <p><strong>Company:</strong> {selectedAssessment.companyName || 'N/A'}</p>
                  <p><strong>Role:</strong> {selectedAssessment.role || 'N/A'}</p>
                  <p><strong>Industry:</strong> {selectedAssessment.industry || 'N/A'}</p>
                  <p><strong>Size:</strong> {selectedAssessment.organizationSize || 'N/A'}</p>
                </div>
              </div>
              
              <div className="details-section">
                <h3>Assessment Results</h3>
                <div className="details-grid">
                  <p><strong>Type:</strong> {getAssessmentTypeLabel(selectedAssessment.assessmentType)}</p>
                  <p><strong>Total Score:</strong> {selectedAssessment.totalScore || 0}/100</p>
                  <p><strong>Result:</strong> {selectedAssessment.resultCategory || 'N/A'}</p>
                </div>
              </div>
              
              {selectedAssessment.answers && selectedAssessment.answers.length > 0 && (
                <div className="details-section">
                  <h3>Question-wise Scores</h3>
                  <div className="table-responsive">
                    <table className="answers-table">
                      <thead>
                        <tr>
                          <th>Question</th>
                          <th>Answer</th>
                          <th>Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedAssessment.answers.map((ans, idx) => (
                          <tr key={idx}>
                            <td>Q{ans.questionNumber}</td>
                            <td>{ans.answer || 'N/A'}</td>
                            <td>{ans.score || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              <div className="details-section">
                <h3>Qualification Questions</h3>
                <div className="details-grid">
                  <p><strong>Priority:</strong> {selectedAssessment.qualification?.priority || 'N/A'}</p>
                  <p><strong>Timeline:</strong> {selectedAssessment.qualification?.timeline || 'N/A'}</p>
                  <p><strong>Investment:</strong> {selectedAssessment.qualification?.investment || 'N/A'}</p>
                  <p><strong>Consultation Interest:</strong> {selectedAssessment.qualification?.consultationInterest || 'N/A'}</p>
                  <p><strong>Additional Info:</strong> {selectedAssessment.qualification?.additionalInfo || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Assessments;